import React, { useMemo } from 'react';
import { Button, Form } from 'react-bootstrap';
import ExternalUserSelector from '../../Components/ExternalUserSelector';
import InternalUserSelector from '../../Components/InternalUserSelector';
import { useTenants } from '../../hooks/tenants/useTenants';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import { useUsersData } from '../../Helpers/UsersContext';
import { ExternalUser } from '../../types/guestAccess';
import { KeyHolderKind, KEY_HOLDER_KINDS, keyHolderKindLabel } from '../../types/keyRegister';

interface KeyHolderPickerProps {
    kind: KeyHolderKind;
    /** tenantKey or user UID; '' when none is picked. */
    holderKey: string;
    error?: string;
    /** The picked external user, for the selector's display. */
    externalUser: ExternalUser | null;
    onKindChange: (kind: KeyHolderKind) => void;
    onPick: (holderKey: string) => void;
    onPickExternal: (user: ExternalUser) => void;
    onCreateExternal: () => void;
    /** Opens the tenant picker, which the caller renders outside its <Form>. */
    onChooseTenant: () => void;
}

/** The hand-out recipient: holder kind radios, then a tenant, app user or external user. */
const KeyHolderPicker: React.FC<KeyHolderPickerProps> = ({
    kind, holderKey, error, externalUser, onKindChange, onPick, onPickExternal, onCreateExternal, onChooseTenant,
}) => {
    const { getTenantName } = useTenants();
    const term = useTenantTerm();
    const tenantWord = term.singular.toLowerCase();
    const { users } = useUsersData();
    const appUsers = useMemo(
        () => (users ?? [])
            .filter(u => !u.isExternal && !u.deleted)
            .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv')),
        [users]
    );

    return (
        <>
            <Form.Group className="mb-3">
                <Form.Label>Mottagare *</Form.Label>
                <div className="key-holder-kinds" role="radiogroup" aria-label="Typ av mottagare">
                    {KEY_HOLDER_KINDS.map(option => (
                        <Form.Check
                            key={option}
                            type="radio"
                            id={`key-holder-kind-${option}`}
                            name="holderKind"
                            label={option === 'external' ? 'Extern' : keyHolderKindLabel(option, term)}
                            checked={kind === option}
                            onChange={() => onKindChange(option)}
                        />
                    ))}
                </div>
            </Form.Group>

            <Form.Group className="mb-3">
                {kind === 'tenant' && (
                    <div className="key-holder-tenant">
                        <span className={holderKey ? '' : 'text-muted'}>
                            {holderKey ? getTenantName(holderKey) : `Ingen ${tenantWord} vald`}
                        </span>
                        <Button variant="outline-primary" size="sm" onClick={onChooseTenant}>
                            {holderKey ? `Byt ${tenantWord}` : `Välj ${tenantWord}`}
                        </Button>
                    </div>
                )}
                {kind === 'user' && (
                    <InternalUserSelector
                        users={appUsers}
                        value={holderKey}
                        onChange={onPick}
                        unassignedLabel="Välj användare..."
                    />
                )}
                {kind === 'external' && (
                    <ExternalUserSelector
                        selectedUser={externalUser}
                        onSelect={onPickExternal}
                        onCreateNew={onCreateExternal}
                    />
                )}
                {error && <div className="invalid-feedback d-block">{error}</div>}
            </Form.Group>
        </>
    );
};

export default KeyHolderPicker;
